import { useEffect, useRef, useState } from 'react';

export default function StrengthsSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  const step2Items = [
    {
      title: 'ボイストレーニング',
      desc: '基礎から丁寧に指導。あなたの声の魅力を最大限に引き出します。',
      icon: 'ri-mic-line',
      image: 'https://public.readdy.ai/ai/img_res/bda05e1abb9f289aa39adf31b817348f.jpg'
    },
    {
      title: '録音技術',
      desc: 'プロ仕様の機材で、あなたの歌声を最高の音質で録音します。',
      icon: 'ri-record-circle-line',
      image: 'https://readdy.ai/api/search-image?query=A%20modern%20recording%20studio%20setup%20with%20professional%20microphone%20and%20audio%20interface%2C%20student%20recording%20vocals%20with%20headphones%2C%20clean%20minimalist%20studio%20environment%2C%20warm%20ambient%20lighting%2C%20high-end%20recording%20equipment%2C%20professional%20music%20production%20atmosphere%2C%20natural%20photography%20style&width=800&height=600&seq=str2recording002&orientation=landscape'
    },
    {
      title: 'MIX・マスタリング',
      desc: '音のバランスを整え、プロ品質の仕上がりに。',
      icon: 'ri-equalizer-line',
      image: 'https://readdy.ai/api/search-image?query=A%20professional%20audio%20mixing%20and%20mastering%20workstation%20with%20digital%20audio%20workstation%20software%20on%20screen%2C%20audio%20mixing%20console%20with%20faders%20and%20knobs%2C%20studio%20monitors%2C%20warm%20professional%20lighting%2C%20modern%20music%20production%20studio%20environment%2C%20clean%20and%20organized%20workspace%2C%20high%20quality%20photography&width=800&height=600&seq=str2mixing003&orientation=landscape'
    },
    {
      title: '動画編集',
      desc: 'かんたん編集から本格編集まで、あなたのレベルに合わせて指導。',
      icon: 'ri-film-line',
      image: 'https://readdy.ai/api/search-image?query=A%20modern%20video%20editing%20workspace%20with%20professional%20editing%20software%20on%20screen%2C%20timeline%20with%20video%20clips%20and%20effects%2C%20color%20grading%20tools%20visible%2C%20creative%20studio%20environment%2C%20warm%20professional%20lighting%2C%20organized%20desk%20setup%20with%20graphics%20tablet%2C%20high%20quality%20photography%2C%20focused%20on%20video%20production%20workflow&width=800&height=600&seq=str2videoediting004&orientation=landscape'
    }
  ];

  return (
    <section id="strengths" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* セクション見出し */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            Size Music Schoolだからできること
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed px-4">
            未経験から最短4カ月で、あなたの「歌ってみた」を完成させる環境です。
          </p>
        </div>

        {/* プロセスフロー */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          {/* モバイル版 - 縦並び */}
          <div className="flex flex-col items-center gap-2 mb-8 sm:mb-12 sm:hidden">
            {['レッスン', 'レコーディング', 'MIX・ピッチ補正', '動画編集', '投稿'].map((step, index) => (
              <div key={index} className="flex flex-col items-center w-full max-w-[200px]">
                <div className="bg-gradient-to-br from-orange-400 to-red-400 text-white px-4 py-2 rounded-lg font-semibold shadow-md text-xs w-full text-center">
                  {step}
                </div>
                {index < 4 && (
                  <i className="ri-arrow-down-line text-xl text-orange-400 my-1"></i>
                )}
              </div>
            ))}
          </div>

          {/* デスクトップ版 - 横並び */}
          <div className="hidden sm:flex flex-wrap justify-center items-center gap-2 sm:gap-4 mb-8 sm:mb-12">
            {['レッスン', 'レコーディング', 'MIX・ピッチ補正', '動画編集', '投稿'].map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg text-xs sm:text-sm md:text-base">
                  {step}
                </div>
                {index < 4 && (
                  <i className="ri-arrow-right-line text-xl sm:text-2xl md:text-3xl text-orange-500 mx-2"></i>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 01: 最短4カ月で完成 */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div className="bg-white p-6 sm:p-10 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-gray-100 relative overflow-hidden">
            {/* キャラクター画像 - 右上に配置 */}
            <div className="absolute top-0 right-0 w-80 h-80 opacity-100 hidden lg:block">
              <img
                src="https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/598e172377213a738fe409bf40515f36.png"
                alt="キャラクター"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-xl sm:text-2xl font-black px-4 sm:px-5 py-2 sm:py-3 rounded-xl shadow-lg">
                01
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 leading-tight">
                未経験でも、最短4カ月で<br className="sm:hidden" />「歌ってみた」1本を完成
              </h3>
            </div>

            <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-8 sm:mb-10 relative z-10">
              Size Music Schoolでは、「歌が好き」からのスタートでも、最短4カ月で『歌ってみた』1本を完成させることをゴールにしています。<br />バラバラに学ぶのではなく、すべてがつながった<strong className="text-orange-600">一気通貫カリキュラム</strong>です。
            </p>

            {/* 4カ月のカリキュラム */}
            <div className="bg-gradient-to-br from-orange-50 to-pink-50 p-5 sm:p-8 rounded-2xl sm:rounded-3xl relative z-10">
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-5 sm:mb-8 text-center flex items-center justify-center gap-2 sm:gap-3">
                <i className="ri-calendar-line text-orange-500 text-xl sm:text-2xl"></i>
                4カ月のカリキュラムの流れ
              </h4>

              <div className="space-y-4 sm:space-y-6">
                {/* 1カ月目 */}
                <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border-2 border-orange-100">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-base sm:text-lg font-black px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1">
                      <h5 className="text-base sm:text-lg font-bold text-gray-800 mb-2">1カ月目</h5>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        歌い方の基礎・発声・音程、あなたの声の「良さ」を知る
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2カ月目 */}
                <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border-2 border-orange-100">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-base sm:text-lg font-black px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <h5 className="text-base sm:text-lg font-bold text-gray-800 mb-2">2カ月目</h5>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        録音の流れを習得（マイクの使い方・録り方・簡単な編集）
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3カ月目 */}
                <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border-2 border-orange-100">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-base sm:text-lg font-black px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <h5 className="text-base sm:text-lg font-bold text-gray-800 mb-2">3カ月目</h5>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        ピッチ補正・MIXで「聴きやすい音」に整える
                      </p>
                    </div>
                  </div>
                </div>

                {/* 4カ月目 */}
                <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border-2 border-orange-100">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white text-base sm:text-lg font-black px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md flex-shrink-0">
                      4
                    </div>
                    <div className="flex-1">
                      <h5 className="text-base sm:text-lg font-bold text-gray-800 mb-2">4カ月目</h5>
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                        動画編集 → サムネ作成 → 投稿まで、一緒に仕上げて完成
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6 bg-orange-100 p-3 sm:p-4 rounded-xl border-l-4 border-orange-500">
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  <i className="ri-information-line text-orange-500 mr-1"></i>
                  ※ペースは人それぞれなので、あくまで「最短の目安」です。ゆっくり進めたい方も大歓迎です。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 02: 一気通貫カリキュラム */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div className="bg-gradient-to-br from-orange-400 to-orange-500 p-6 sm:p-10 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-orange-300">
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 flex items-center justify-center bg-white text-orange-500 text-2xl sm:text-3xl font-black rounded-xl sm:rounded-2xl shadow-lg">
                02
              </div>
              <div>
                <h3 className="text-xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                  歌い手に必要なこと、すべて学べる<br />一気通貫カリキュラム
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-lg text-white/95 mb-6 sm:mb-10 leading-relaxed">
              歌い方の基礎から、録音、ピッチ補正、MIX、動画編集、そして投稿準備まで。歌い手に必要なステップを、最初から最後まで一つの流れで学べます。
            </p>

            <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-6 rounded-xl sm:rounded-2xl mb-6 sm:mb-8 border border-white/20">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <i className="ri-time-line text-xl sm:text-2xl text-white"></i>
                <span className="text-base sm:text-xl font-bold text-white">最短4カ月</span>
              </div>
              <p className="text-xs sm:text-base text-white/90 leading-relaxed">
                完全オリジナル動画が完成<br />
                録音から動画編集、投稿まで全てサポート
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 text-orange-500 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                  <i className="ri-mic-line text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2">録音</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  スタジオでの本格的なレコーディング、ボーカル録音の基礎
                </p>
              </div>

              <div className="bg-white/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 text-orange-500 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                  <i className="ri-equalizer-line text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2">MIX</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  音量調整、エフェクト処理、音質向上のテクニック
                </p>
              </div>

              <div className="bg-white/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 text-orange-500 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                  <i className="ri-sound-module-line text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2">マスタリング</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  最終調整、音圧調整、プロ品質への仕上げ
                </p>
              </div>

              <div className="bg-white/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 text-orange-500 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                  <i className="ri-movie-line text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2">動画編集</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  素材のカット、テロップ、簡単なエフェクト、サムネイル作成
                </p>
              </div>

              <div className="bg-white/95 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-orange-100 text-orange-500 rounded-lg sm:rounded-xl mb-3 sm:mb-4">
                  <i className="ri-upload-cloud-line text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2">投稿準備</h4>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  動画書き出し、YouTubeや各プラットフォームへの投稿方法
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 03: 動画編集コース */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-gray-100">
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center bg-orange-500 text-white text-xl sm:text-2xl md:text-3xl font-black rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
                03
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  動画編集は「かんたんコース」と<br />「本格コース」の2種類
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-6 sm:mb-8 md:mb-10 leading-relaxed">
              動画編集も、ゴールに合わせて2つのコースをご用意しています。
            </p>

            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* かんたんコース */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 sm:p-8 rounded-xl sm:rounded-2xl border-2 border-gray-200">
                <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">
                  かんたんコース
                </h4>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  「まずは1本、歌ってみたを投稿したい人」向け
                </p>

                <div className="mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    このコースで作れる動画のイメージ
                  </p>
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg sm:rounded-xl"
                      src="https://www.youtube.com/embed/CGRhYihBuqw"
                      title="かんたんコース動画イメージ"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl border-2 border-gray-200">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    「難しいことはさておき、とにかく1本出してみたい」<br />
                    そんな方が、最短距離で初投稿まで行けるコースです。
                  </p>
                </div>
              </div>

              {/* 本格コース */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 sm:p-8 rounded-xl sm:rounded-2xl border-2 border-gray-200">
                <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">
                  本格コース
                </h4>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  「MVのような、こだわった1本を作りたい人」向け
                </p>

                <div className="mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    このコースで作れる動画のイメージ
                  </p>
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg sm:rounded-xl"
                      src="https://www.youtube.com/embed/NpsBG6BQqpM"
                      title="本格コース動画イメージ"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl border-2 border-gray-200">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    「こだわりの1本を作りたい」<br />
                    そんな方のために、本格的な動画編集スキルを身につけられるコースです。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 04: 初心者サポート */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-orange-400 relative overflow-hidden">
            {/* キャラクター画像 - 右上に配置 */}
            <div className="absolute top-0 right-0 w-80 h-80 opacity-100 hidden lg:block">
              <img
                src="https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/8ffdfab8b29986e53e0d3b1e6f4f523a.png"
                alt="キャラクター"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-8 relative z-10">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center bg-white text-orange-500 text-lg sm:text-2xl md:text-3xl font-black rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
                04
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  初心者がつまずくポイントを、<br className="hidden sm:block" />最初から知っています
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-lg text-white/95 leading-relaxed mb-4 sm:mb-8 relative z-10">
              「全部教える」より「つまずかせない」ことを大切にしています。
            </p>

            <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-8 relative z-10">
              {[
                { icon: 'ri-lightbulb-line', title: '先回りサポート', desc: '迷うポイントを先回りしてサポート' },
                { icon: 'ri-tools-line', title: '機材選定サポート', desc: '最初から一緒に選定' },
                { icon: 'ri-computer-line', title: 'ソフト使い方指導', desc: 'つまずきやすい箇所から丁寧に' },
                { icon: 'ri-question-answer-line', title: '不安の即時解消', desc: 'その場で解消できる環境' }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2 sm:gap-4 bg-white/95 p-3 sm:p-5 rounded-lg sm:rounded-xl shadow-lg backdrop-blur-sm">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-base sm:text-2xl text-white`}></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-800 mb-0.5 text-xs sm:text-base">{item.title}</div>
                    <div className="text-xs sm:text-sm text-gray-600">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/95 p-3 sm:p-6 rounded-lg sm:rounded-2xl text-center shadow-lg backdrop-blur-sm relative z-10">
              <p className="text-sm sm:text-lg md:text-xl font-bold text-orange-600 mb-1 sm:mb-2">
                「次に何をすればいいか」が、いつも明確
              </p>
              <p className="text-xs sm:text-base md:text-lg text-gray-700">
                迷わせないカリキュラムです
              </p>
            </div>
          </div>
        </div>

        {/* 05: 自分のペース */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-gray-100 relative overflow-hidden">
            {/* キャラクター画像 - 右上に配置 */}
            <div className="absolute top-0 right-0 w-80 h-80 opacity-100 hidden lg:block z-0">
              <img
                src="https://static.readdy.ai/image/f1e8e8819fa7a9a02132dd8ff3c38992/3660165aa63a41dfefe691cc158ab819.png"
                alt="キャラクター"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 relative z-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500 text-white text-xl sm:text-2xl md:text-3xl font-black rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
                05
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  「自分のペース」で<br />
                  進められる
                </h3>
              </div>
            </div>

            <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-6 sm:mb-8 relative z-10">
              早く進みたい人も、ゆっくり確実に進みたい人も。<br />
              あなたのペースを尊重します。焦る必要はありません。
            </p>

            <div className="space-y-4 sm:space-y-6 relative z-10">
              {[
                { icon: 'ri-calendar-check-line', title: '柔軟なスケジュール', desc: 'あなたの生活リズムに合わせて調整可能' },
                { icon: 'ri-user-settings-line', title: '個別カリキュラム', desc: '理解度に合わせて進行速度を調整' },
                { icon: 'ri-heart-pulse-line', title: '無理のない継続', desc: '無理なく続けられる環境があります' }
              ].map((item, index) => (
                <div key={index} className="bg-gradient-to-r from-orange-50 to-amber-50 p-5 sm:p-6 md:p-8 rounded-xl border-2 border-orange-100">
                  <div className="flex items-start gap-3 sm:gap-4 md:gap-6">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-xl sm:text-2xl md:text-3xl text-white`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-1 sm:mb-2 md:mb-3">{item.title}</div>
                      <div className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 最終メッセージ */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl text-center shadow-2xl">
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">
            あなたの「歌ってみたい」を、<br />
            「歌ってみた」に変える場所です
          </p>
          <p className="text-base sm:text-lg text-white/95 leading-relaxed">
            一緒に、最初の一歩を踏み出しましょう
          </p>
        </div>
      </div>
    </section>
  );
}